import { createContext, useContext, useRef, useState, useCallback } from 'react'

export interface JobMeta {
  videoTitle: string
  videoUrl: string
  videoThumbnailUrl: string
  targetLang: string
  sourceLang: string
  durationSeconds: number
  speakerProfiles?: { label: string; gender: string }[]
  originalStoragePath?: string | null
}

export interface PlayerState {
  jobId: string | null
  audioUrl: string | null
  jobMeta: JobMeta | null
  playing: boolean
  currentTime: number
  duration: number
  volume: number
  miniOpen: boolean
  fullScreenOpen: boolean
}

export interface PlayerActions {
  openMiniPlayer: (jobId: string, audioUrl: string, meta?: JobMeta) => void
  openFullScreen: () => void
  closePlayers: () => void
  togglePlay: () => void
  seek: (time: number) => void
  setVolume: (vol: number) => void
  skip: (seconds: number) => void
}

export type PlayerContextType = PlayerState & PlayerActions

const defaultState: PlayerState = {
  jobId: null,
  audioUrl: null,
  jobMeta: null,
  playing: false,
  currentTime: 0,
  duration: 0,
  volume: 0.9,
  miniOpen: false,
  fullScreenOpen: false,
}

export const PlayerContext = createContext<PlayerContextType>({
  ...defaultState,
  openMiniPlayer: () => {},
  openFullScreen: () => {},
  closePlayers: () => {},
  togglePlay: () => {},
  seek: () => {},
  setVolume: () => {},
  skip: () => {},
})

export function usePlayer() {
  return useContext(PlayerContext)
}

export function usePlayerProvider() {
  const [state, setState] = useState<PlayerState>(defaultState)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const openMiniPlayer = useCallback((jobId: string, audioUrl: string, meta?: JobMeta) => {
    if (audioRef.current) {
      audioRef.current.pause()
    }
    const audio = new Audio(audioUrl)
    audio.volume = state.volume
    audioRef.current = audio

    audio.addEventListener('loadedmetadata', () => {
      setState(s => ({ ...s, duration: audio.duration }))
    })
    audio.addEventListener('timeupdate', () => {
      setState(s => ({ ...s, currentTime: audio.currentTime }))
    })
    audio.addEventListener('ended', () => {
      setState(s => ({ ...s, playing: false, currentTime: 0 }))
    })

    setState(s => ({
      ...s,
      jobId,
      audioUrl,
      jobMeta: meta ?? null,
      miniOpen: true,
      fullScreenOpen: false,
      playing: false,
      currentTime: 0,
      duration: 0,
    }))
  }, [state.volume])

  const openFullScreen = useCallback(() => {
    setState(s => ({ ...s, fullScreenOpen: true }))
  }, [])

  const closePlayers = useCallback(() => {
    audioRef.current?.pause()
    setState(s => ({
      ...s,
      miniOpen: false,
      fullScreenOpen: false,
      playing: false,
    }))
  }, [])

  const togglePlay = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) {
      void audio.play()
      setState(s => ({ ...s, playing: true }))
    } else {
      audio.pause()
      setState(s => ({ ...s, playing: false }))
    }
  }, [])

  const seek = useCallback((time: number) => {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = time
    setState(s => ({ ...s, currentTime: time }))
  }, [])

  const setVolume = useCallback((vol: number) => {
    if (audioRef.current) audioRef.current.volume = vol
    setState(s => ({ ...s, volume: vol }))
  }, [])

  const skip = useCallback((seconds: number) => {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = Math.max(0, Math.min(audio.duration, audio.currentTime + seconds))
  }, [])

  return {
    value: {
      ...state,
      openMiniPlayer,
      openFullScreen,
      closePlayers,
      togglePlay,
      seek,
      setVolume,
      skip,
    },
  }
}
